
export default {
  name: 'DecorativeDiacritics',
  props: {
    density: {
      type: String,
      default: 'low', // 'low', 'medium', 'high'
    },
    areas: {
      type: Array,
      default: () => ['top-left', 'top-right', 'bottom-left', 'bottom-right']
    },
    minDistance: {
      type: Number,
      default: 150 // Minimum pixels between marks
    },
    parallaxStrength: {
      type: Number,
      default: 0.3 // Multiplier for parallax effect (0 = none, 1 = full speed)
    },
    opacity: {
      type: Number,
      default: 0.5
    },
    dark: {
      type: String,
      default: 'false'
    }
  },
  data() {
    return {
      diacriticCharacters: ['~', '¨', '˚'],
      colors: [
        { name: 'blue', value: '#309DB6', opacity: this.opacity },
        { name: 'green', value: '#2EA18B', opacity: this.opacity },
        { name: 'dark-blue', value: '#395674', opacity: this.opacity },
        { name: 'yellow', value: '#FBC14A', opacity: this.opacity },
        { name: 'coral', value: '#F76386', opacity: this.opacity },
        { name: 'purple', value: '#83426C', opacity: this.opacity },
      ],
      darkColors: [
        { name: 'blue', value: '#309DB6', opacity: this.opacity },
        { name: 'green', value: '#2EA18B', opacity: this.opacity },
        { name: 'yellow', value: '#FBC14A', opacity: this.opacity },
        { name: 'coral', value: '#F76386', opacity: this.opacity },
        { name: 'purple', value: '#FFF', opacity: this.opacity },
      ],
      pastelColors: [
        { name: 'blue', value: '#F0FAFF', opacity: 0.75 },
        { name: 'green', value: '#F0FFF6', opacity: 0.75 },
        { name: 'yellow', value: '#FFFCF0', opacity: 0.75 },
        { name: 'coral', value: '#FFF0F7', opacity: 0.75 },
        { name: 'purple', value: '#F9F0FF', opacity: 0.75 },
      ],
      diacriticMarks: [],
      scrollY: 0
    }
  },
  computed: {
    markCount() {
      const counts = {
        low: 2,
        medium: 6,
        high: 12
      }
      return counts[this.density] || counts.medium
    }
  },
  mounted() {
    this.generateDiacritics()
    window.addEventListener('scroll', this.handleScroll, { passive: true })
    this.handleScroll() // Initialize positions
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    handleScroll() {
      this.scrollY = window.scrollY
      this.updateParallax()
    },
    
    updateParallax() {
      this.diacriticMarks = this.diacriticMarks.map(mark => ({
        ...mark,
        parallaxOffset: this.scrollY * mark.parallaxSpeed
      }))
    },
    
    generateDiacritics() {
      const marks = []
      const placedPositions = []
      const maxAttempts = 100
      
      // Calculate how many marks per area for even distribution
      const marksPerArea = Math.ceil(this.markCount / this.areas.length)
      
      // Distribute marks evenly across areas
      this.areas.forEach((area, areaIndex) => {
        const marksForThisArea = Math.min(
          marksPerArea, 
          this.markCount - marks.length
        )
        
        for (let i = 0; i < marksForThisArea; i++) {
          let attempts = 0
          let validPosition = false
          let position, color, character, size
          
          while (!validPosition && attempts < maxAttempts) {
            position = this.getPositionForArea(area)
            size = this.getRandomSize()
            color = this.dark === false ? this.colors[Math.floor(Math.random() * this.colors.length)] : this.darkColors[Math.floor(Math.random() * this.darkColors.length)]
            character = this.diacriticCharacters[Math.floor(Math.random() * this.diacriticCharacters.length)]
            
            // Convert percentage to pixels (assuming viewport dimensions)
            const x = (parseFloat(position.left) / 100) * window.innerWidth
            const y = (parseFloat(position.top) / 100) * window.innerHeight
            
            // Check if this position overlaps with existing marks
            validPosition = this.isValidPosition(x, y, placedPositions, size)
            attempts++
          }
          
          if (validPosition) {
            const mark = {
              id: marks.length,
              character,
              ...position,
              size,
              color: color.value,
              opacity: color.opacity,
              rotation: Math.random() * 30,
              // Vary parallax speed for depth effect
              parallaxSpeed: (Math.random() * 0.4 - 0.2) * this.parallaxStrength,
              parallaxOffset: 0
            }
            
            marks.push(mark)
            placedPositions.push({
              x: (parseFloat(position.left) / 100) * window.innerWidth,
              y: (parseFloat(position.top) / 100) * window.innerHeight,
              size: this.getSizeInPixels(size)
            })
          }
        }
      })
      
      this.diacriticMarks = marks
    },
    
    isValidPosition(x, y, placedPositions, size) {
      const sizeInPx = this.getSizeInPixels(size)
      
      for (const placed of placedPositions) {
        const distance = Math.sqrt(
          Math.pow(x - placed.x, 2) + Math.pow(y - placed.y, 2)
        )
        
        // Consider the size of both marks plus minimum distance
        const requiredDistance = (sizeInPx + placed.size) / 2 + this.minDistance
        
        if (distance < requiredDistance) {
          return false
        }
      }
      
      return true
    },
    
    getSizeInPixels(size) {
      // Convert rem to pixels (assuming 1rem = 16px)
      const remValue = parseFloat(size)
      return remValue * 16
    },
    
    getPositionForArea(area) {
      const positions = {
        'top-left': {
          top: `${5 + Math.random() * 25}%`,
          left: `${5 + Math.random() * 15}%`
        },
        'top-right': {
          top: `${5 + Math.random() * 25}%`,
          left: `${80 + Math.random() * 15}%`
        },
        'bottom-left': {
          top: `${70 + Math.random() * 25}%`,
          left: `${5 + Math.random() * 15}%`
        },
        'bottom-right': {
          top: `${70 + Math.random() * 25}%`,
          left: `${80 + Math.random() * 15}%`
        },
        'center-left': {
          top: `${35 + Math.random() * 30}%`,
          left: `${5 + Math.random() * 10}%`
        },
        'center-right': {
          top: `${35 + Math.random() * 30}%`,
          left: `${85 + Math.random() * 10}%`
        }
      }
      
      return positions[area] || positions['top-left']
    },
    
    getRandomSize() {
      const sizes = ['14rem', '10rem', '16rem', '18rem', '8rem', '12rem']
      return sizes[Math.floor(Math.random() * sizes.length)]
    }
  }
}
